import React from 'react'
import * as XLSX from 'xlsx'
import './FleetOemReport.css'
import CyberButton from './CyberButton'

const COMM_STATUSES = new Set(['Communicating', 'Communicating but Vehicle Off / No Network'])

function buildRows(vehicles) {
  const map = {}
  for (const v of vehicles) {
    if (v.mg?.drive_status !== 'Deployable' || v.mg?.is_active !== 'Y') continue
    const oem = v.oem || 'Unknown'
    if (!map[oem]) map[oem] = { oem, total: 0, comm: 0, ign12: 0, nonComm: 0, noApi: 0 }
    const r = map[oem]
    r.total++
    if (v.status === 'No API') r.noApi++
    else if (COMM_STATUSES.has(v.status)) r.comm++
    else if (v.status === 'More than 12 hours ignition off') r.ign12++
    else if (v.status === 'Non-Communicating') r.nonComm++
  }
  return Object.values(map).sort((a, b) => b.total - a.total)
}

function buildSummary(rows) {
  return rows.reduce(
    (acc, r) => ({
      total: acc.total + r.total,
      comm: acc.comm + r.comm,
      ign12: acc.ign12 + r.ign12,
      nonComm: acc.nonComm + r.nonComm,
      noApi: acc.noApi + r.noApi,
    }),
    { total: 0, comm: 0, ign12: 0, nonComm: 0, noApi: 0 }
  )
}

const VEHICLE_COLUMNS = [
  { key: 'rn', label: 'Registration No.' },
  { key: 'cn', label: 'Chassis No.' },
  { key: 'oem', label: 'OEM' },
  { key: 'mdl', label: 'Model' },
  { label: 'Vehicle Type', get: v => v.mg?.vehicle_type || '-' },
  { label: 'Drive Status', get: v => v.mg?.drive_status || '-' },
  { label: 'City', get: v => v.mg?.city || '-' },
  { label: 'Location', get: v => v.vm?.location || '-' },
  { label: 'Charging Type', get: v => v.vm?.charging_type || '-' },
  { label: 'Motor No.', get: v => v.vm?.motor_number || '-' },
  { label: 'Punch In', get: v => v.punch_in || '-' },
  { label: 'Punch Out', get: v => v.punch_out || '-' },
  { label: 'Status', get: v => COMM_STATUSES.has(v.status) ? 'Communicating' : (v.status ?? '') },
  { key: 'ig', label: 'Ignition', fmt: v => (v ? 'ON' : 'OFF') },
  { key: 'vs', label: 'Vehicle Status' },
  { key: 'csp', label: 'Speed (km/h)' },
  { key: 'soc', label: 'SOC (%)' },
  { key: 'soh', label: 'SOH (%)' },
  { key: 'dte', label: 'DTE (km)' },
  { key: 'od', label: 'Odometer (km)' },
  { key: 'vbv', label: 'Battery Voltage (V)' },
  { key: 'vbc', label: 'Battery Current' },
  { key: 'vbt', label: 'Battery Temp' },
  { key: 'vbe', label: 'Battery Energy' },
  { key: 'gdir', label: 'GPS Direction' },
  { key: 'lt', label: 'Latitude' },
  { key: 'lng', label: 'Longitude' },
  { key: 'dts', label: 'CAN Data Time Stamp (IST)' },
  { key: 'rts', label: 'Server Data Time Stamp (IST)' },
  { key: 'gdts', label: 'GPS Data Time Stamp (IST)' },
  { key: 'grts', label: 'GPS Server Time Stamp (IST)' },
  { key: 'difference_in_hours', label: 'Hours Since Last Data' },
]

function exportReport(rows, summary, vehicles) {
  const now = new Date()
  const pad = n => String(n).padStart(2, '0')
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`
  const wb = XLSX.utils.book_new()

  // Sheet 1: OEM summary counts
  const headers = ['OEM Name', 'Total Deployable Vehicles', 'Communicating', 'More than 12 hours ignition off', 'Non-Communicating', 'NO API']
  const summaryRow = ['Total', summary.total, summary.comm, summary.ign12, summary.nonComm, summary.noApi]
  const dataRows = rows.map(r => [r.oem, r.total, r.comm, r.ign12, r.nonComm, r.noApi])
  const ws1 = XLSX.utils.aoa_to_sheet([summaryRow, [], headers, ...dataRows])
  ws1['!cols'] = [20, 28, 18, 38, 22, 12].map(wch => ({ wch }))
  XLSX.utils.book_append_sheet(wb, ws1, 'Fleet Status Report')

  // Sheet 2: Full vehicle data for deployable active vehicles
  const deployable = (vehicles || []).filter(v => v.mg?.drive_status === 'Deployable' && v.mg?.is_active === 'Y')
  if (deployable.length > 0) {
    const vehicleRows = deployable.map(v =>
      Object.fromEntries(
        VEHICLE_COLUMNS.map(({ key, label, fmt, get }) => [
          label,
          get ? get(v) : fmt ? fmt(v[key]) : (v[key] ?? ''),
        ])
      )
    )
    const ws2 = XLSX.utils.json_to_sheet(vehicleRows)
    ws2['!cols'] = VEHICLE_COLUMNS.map(({ label }) => ({ wch: Math.max(label.length + 4, 18) }))
    ws2['!freeze'] = { xSplit: 0, ySplit: 1 }
    XLSX.utils.book_append_sheet(wb, ws2, 'Vehicle Data')
  }

  XLSX.writeFile(wb, `fleet_status_report_${stamp}.xlsx`)
}

const thStyle = (bg, color) => ({
  background: bg,
  color: color,
  fontWeight: 700,
  padding: '10px 14px',
  textAlign: 'center',
  fontSize: 12,
  whiteSpace: 'nowrap',
  border: '1px solid #424666',
})

const tdStyle = (align = 'center') => ({
  padding: '9px 14px',
  textAlign: align,
  fontSize: 14,
  border: '1px solid #2e3358',
  color: '#fff',
})

const summaryTdStyle = (bg, color) => ({
  ...tdStyle(),
  background: bg,
  color: color,
  fontWeight: 700,
  fontSize: 14,
})

export default function FleetOemReport({
  vehicles,
  cityFilter, setCityFilter, cityOptions,
  hubFilter, setHubFilter, hubOptions,
  vehicleTypeFilter, setVehicleTypeFilter, vehicleTypeOptions,
  statusFilter, setStatusFilter,
}) {
  const rows = buildRows(vehicles)
  const summary = buildSummary(rows)

  return (
    <div style={{ marginTop: 28 }}>
      <h2 style={{ margin: '0 0 10px 0', fontSize: 16, fontWeight: 700, color: '#fff', letterSpacing: 0.2 }}>
        Fleet Status Report
      </h2>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 8 }}>
        {/* Filters row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap' }}>Status</span>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{ height: 30, borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid #424666', background: '#1a1e35', color: '#fff', padding: '0 8px', cursor: 'pointer', outline: 'none', minWidth: 160 }}
          >
            {[
              { value: 'All', label: 'All' },
              { value: 'Communicating', label: 'Communicating' },
              { value: 'Non-Communicating', label: 'Non-Communicating' },
              { value: 'No API', label: 'No API' },
              { value: '>12h Ignition Off', label: '> 12h Ignition Off' },
            ].map(({ value, label }) => (
              <option key={value} value={value} style={{ background: '#1a1e35', color: '#fff' }}>{label}</option>
            ))}
          </select>

          <span style={{ fontSize: 12, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap' }}>Vehicle Type</span>
          <select
            value={vehicleTypeFilter}
            onChange={e => setVehicleTypeFilter(e.target.value)}
            style={{ height: 30, borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid #424666', background: '#1a1e35', color: '#fff', padding: '0 8px', cursor: 'pointer', outline: 'none', minWidth: 110 }}
          >
            {vehicleTypeOptions.map(t => (
              <option key={t} value={t} style={{ background: '#1a1e35', color: '#fff' }}>{t}</option>
            ))}
          </select>

          <span style={{ fontSize: 12, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap' }}>City</span>
          <select
            value={cityFilter}
            onChange={e => setCityFilter(e.target.value)}
            style={{ height: 30, borderRadius: 6, fontSize: 12, border: '1px solid #424666', background: '#1a1e35', color: '#fff', padding: '0 8px', cursor: 'pointer', outline: 'none', minWidth: 140 }}
          >
            {cityOptions.map(c => <option key={c} value={c} style={{ background: '#1a1e35', color: '#fff' }}>{c}</option>)}
          </select>

          <span style={{ fontSize: 12, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap' }}>Hub</span>
          <select
            value={hubFilter}
            onChange={e => setHubFilter(e.target.value)}
            style={{ height: 30, borderRadius: 6, fontSize: 12, border: '1px solid #424666', background: '#1a1e35', color: '#fff', padding: '0 8px', cursor: 'pointer', outline: 'none', minWidth: 160 }}
          >
            {hubOptions.map(h => <option key={h} value={h} style={{ background: '#1a1e35', color: '#fff' }}>{h}</option>)}
          </select>

          {(cityFilter !== 'All cities' || hubFilter !== 'All hubs' || vehicleTypeFilter !== 'All' || statusFilter !== 'All') && (
            <CyberButton onClick={() => { setCityFilter('All cities'); setHubFilter('All hubs'); setVehicleTypeFilter('All'); setStatusFilter('All') }}>
              Clear all
            </CyberButton>
          )}
        </div>

        <CyberButton onClick={() => exportReport(rows, summary, vehicles)}>
          Export Excel
        </CyberButton>
      </div>

      <div style={{
        overflowX: 'auto', borderRadius: 8,
        border: '1px solid #424666',
        background: 'linear-gradient(180deg, #252a4a 0%, #1e2240 100%)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
      }}>
        <table className="fleet-oem-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#252a4a' }}>
              <th style={{ ...thStyle('#252a4a', '#1a1e35'), textAlign: 'left', minWidth: 140 }}>OEM Name</th>
              <th style={thStyle('#5241a0', '#e9d5ff')}>Total Deployable</th>
              <th style={thStyle('#1a6347', '#6ee7b7')}>Communicating</th>
              <th style={thStyle('#614a00', '#fde68a')}>&gt; 12h Ignition Off</th>
              <th style={thStyle('#612525', '#fca5a5')}>Non-Communicating</th>
              <th style={thStyle('#1a4d78', '#93c5fd')}>No API</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.oem} style={{ background: i % 2 === 0 ? '#1e2240' : '#252a4a' }}>
                <td style={{ ...tdStyle('left'), fontWeight: 600, color: '#fff' }}>{r.oem}</td>
                <td style={{ ...tdStyle(), fontWeight: 700, color: '#fff' }}>{r.total}</td>
                <td style={{ ...tdStyle(), fontWeight: 600, color: '#fff' }}>{r.comm}</td>
                <td style={{ ...tdStyle(), fontWeight: 600, color: '#fff' }}>{r.ign12}</td>
                <td style={{ ...tdStyle(), fontWeight: 600, color: '#fff' }}>{r.nonComm}</td>
                <td style={{ ...tdStyle(), fontWeight: 600, color: '#fff' }}>{r.noApi}</td>
              </tr>
            ))}
            <tr style={{ background: '#131627' }}>
              <td style={{ ...tdStyle('left'), fontWeight: 700, color: '#fff', fontSize: 14 }}>Total</td>
              <td style={summaryTdStyle('rgba(109,40,217,0.45)', '#fff')}>{summary.total}</td>
              <td style={summaryTdStyle('rgba(5,150,105,0.40)', '#fff')}>{summary.comm}</td>
              <td style={summaryTdStyle('rgba(180,130,0,0.40)', '#fff')}>{summary.ign12}</td>
              <td style={summaryTdStyle('rgba(220,38,38,0.40)', '#fff')}>{summary.nonComm}</td>
              <td style={summaryTdStyle('rgba(14,116,144,0.45)', '#fff')}>{summary.noApi}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
